// Public types for the mission-visits API. Lives next to the action
// implementations so consumers can split type-only imports from value
// imports without depending on the full transitive surface.

import type {
  VisitReportSubmitRequest as VisitReportSubmitWire,
  VisitReportV2SubmitRequest as VisitReportV2SubmitWire,
  VisitReportV8SubmitRequest as VisitReportV8SubmitWire,
  VisitReportV8Result as VisitReportV8ResultWire,
} from '../wire/generated/types.generated'
import type { VisitReportDeadLetter } from './visitReportDeadLetter'

// Visit-report enum types — SOURCED FROM THE GENERATED WIRE (SL OpenAPI SSoT,
// regenerated by `pnpm gen:contracts`). These RETIRE the previously hand-rolled
// string-literal unions that drifted from SL PR #1633's tightened enums
// (FR-FE-VISIT-REPORT-TYPED-AT-CAPTURE-CODEGEN-2026-06-26). NonNullable strips
// the `| null | undefined` the wire field carries, leaving the literal union;
// the typed columns below add `| null` back where the payload is nullable.
export type VisitAccessResult = NonNullable<VisitReportSubmitWire['access_result']>
export type VisitConversationResult = NonNullable<VisitReportSubmitWire['conversation_result']>
export type VisitRepCustomerStatus = NonNullable<VisitReportSubmitWire['rep_customer_status']>

export type SkipReason =
  | 'closed_permanently'
  | 'wrong_address'
  | 'refused_meeting'
  | 'duplicate'
  | 'out_of_territory'
  | 'other'

export type LeadRejectionReason =
  | 'wrong_location'
  | 'address_not_found'
  | 'address_invalid'
  | 'business_closed'
  | 'business_dissolved'
  | 'existing_customer'
  | 'recently_visited'
  | 'duplicate'
  | 'appointment_rejected'
  | 'visit_later'
  | 'dm_not_available'
  | 'out_of_territory'
  | 'other'
  // Legacy values kept for backward compatibility — older clients still
  // emit these and the service layer accepts them.
  | 'too_small_irrelevant'
  | 'wrong_data'

export interface VisitLeadRequest {
  visited_at?: string
  state_reason?: string
}

export interface SkipLeadRequest {
  state_reason: SkipReason
}

export interface RejectLeadRequest {
  rejection_reason: LeadRejectionReason
  snooze_until?: string
}

export interface RejectMissionRequest {
  reason?: string
}

export interface VisitReportPayload {
  mission_lead_id: string
  mission_id?: string
  unified_lead_id?: string
  // V1.3: Access level. Optional+nullable since v1.7: the config-driven
  // questionnaire (answers blob) supersedes these typed columns (EC ac851459 —
  // the versioned question set IS the schema), and visit_reports_v2.access_result
  // is nullable. The v1.3 bespoke path still always sends it.
  access_result?: VisitAccessResult | null
  no_access_reason?: string | null
  no_access_detail?: string | null
  no_dm_reason?: string | null
  no_dm_detail?: string | null
  // V1.3: Conversation
  conversation_result?: VisitConversationResult | null
  rejection_reason?: string | null
  rejection_detail?: string | null
  // V1.3: Sale / demo
  demo_given?: boolean | null
  follow_up_scheduled?: boolean | null
  follow_up_date?: string | null
  sale_amount_estimated?: number | null
  // V1.3: Products
  products_presented?: string[]
  products_sold?: string[]
  products_interested?: string[]
  // V1.3: Use cases
  use_cases_confirmed?: string[]
  // V1.3: Contact
  contact_first_name?: string | null
  contact_last_name?: string | null
  contact_role?: string | null
  contact_is_decision_maker?: boolean | null
  contact_card_photo?: string | null
  // V1.3: Type check. Optional+nullable since v1.7 (see access_result).
  type_correct?: 'yes' | 'wrong' | null
  type_override?: string | null
  // V1.3: Status + wrap-up
  rep_customer_status?: VisitRepCustomerStatus | null
  visit_type?: 'termin' | 'kaltbesuch' | null
  next_steps?: 'send_offer' | 'deliver_test' | 'schedule_followup' | 'none' | null
  notes?: string | null
  // System snapshot (auto)
  system_type_prediction?: string | null
  system_product_recommendations?: Record<string, unknown> | null
  system_use_cases?: string[] | null
  // SL-ANCHOR-004: VIP recommendation from visit report
  anchor_recommendation?: 'promote' | 'demote' | 'keep' | null
  anchor_reason?: string | null
  // Timing + meta
  visit_started_at?: string | null
  visit_ended_at?: string | null
  submitted_at?: string
  defer_count?: number
  questionnaire_version?: string
  // v1.7 config-driven questionnaire: answers keyed by the active config's
  // question.key (visit_reports_v2.answers jsonb). The blob IS the schema —
  // no typed-column-per-question (EC ac851459 / SL 2026-06-15).
  answers?: Record<string, string>
  dynamic_context?: Record<string, unknown>
}

// SF-008 thread: the visit-report submit endpoint returns the newly
// created row's id so the FE can route to ScanOrder with a real FK.
// Field is nullable — when the SL response omits it (older deployments,
// queued offline submits), consumers MUST treat the camera path as
// "no FK known yet" rather than fabricating an id (D-33 R3).
export interface VisitReportSubmitResponse {
  visit_report_id: string | null
}

// ── Visit-report v2 (typed Besuchsbericht) ───────────────────────────
//
// The v2 write path (POST /v1/missions/visit-report-v2, model
// VisitReportV2SubmitRequest, additionalProperties:false → extra='forbid').
// The payload type is the GENERATED wire type verbatim (SL OpenAPI SSoT,
// merged PR #1677): the required head (source_event_id,
// mission_lead_id, mission_id, unified_lead_id, sold + the 5 typed CHECK
// dims lead_valid/branche_correct/spin_correct/product_correct/dm_info_correct
// as required {ja|nein|unbekannt} enums) is enforced by the inferred type, so
// the builder cannot omit a required key. We do NOT hand-roll — bind the
// codegen so a contract change flows through `pnpm gen:contracts`.
export type VisitReportV2Payload = VisitReportV2SubmitWire

// Mirrors VisitReportSubmitResponse: the v2 submit returns the new row id so
// the FE can thread the FK (null when queued offline / id absent — D-33 R3).
export interface VisitReportV2SubmitResponse {
  visit_report_id: string | null
  /** Set when the door held the report as a dead letter (the lead resolves to no account):
   *  accepted-terminal, no engagement (visitReportDeadLetter). null otherwise. */
  dead_letter: VisitReportDeadLetter | null
}

// ── Visit-report v8 (the v8 answers door) ────────────────────────────
//
// POST /v1/missions/visit-report-v8, which the provider routes to
// public.rpc_submit_visit_v8_answers (raider-intel DESIGN (a)/(d); it replaces the
// call to rpc_submit_visit_v8_typed). Same `source_event_id` as the v2 report; identity
// is `unified_lead_id` only (the service layer derives account_ref).
//
// TYPED BY TWO SOURCES, NEITHER HAND-ROLLED: the HEAD the generated
// VisitReportV8SubmitRequest already declares is the generated type; every other field
// is the consumer's DEMAND, declared in the FE consumer contract
// (contracts/pact/visit-report-v8/answer-destinations.v8.json) and pinned in the PACT as
// an expected-fail against the generated model until SL's regenerated OpenAPI lands.
// The encoder refuses any field the contract does not declare.
export type VisitReportV8Payload =
  Pick<VisitReportV8SubmitWire, 'source_event_id' | 'mission_id' | 'unified_lead_id'>
  & { readonly [demandField: string]: unknown }

// The RPC receipt, returned inside the SL success envelope
// (`{ success: true, data: VisitReportV8Result }`): the per-request counts plus the
// stored_* READ-BACK of what is persisted, the SL-derived account_ref /
// stored_account_ref, the engagement `revision` and `submitted_at`. Typed straight
// from the generated wire so a contract change surfaces at compile time.
export type VisitReportV8Result = VisitReportV8ResultWire

export interface ComplianceStatus {
  compliant: boolean
  open_reports: Array<{
    lead_id: string
    lead_name: string
    visited_at: string
  }>
  used_deferrals: number
  max_deferrals: number
  is_lockout: boolean
  is_morning_lockout: boolean
}

export interface OptimizeRouteResult {
  mission_id: string
  route_polyline: string
  route_distance_km: number
  route_duration_min: number
  optimized: boolean
  traffic_aware: boolean
}

export interface AnchorEvent {
  id: string
  action: 'promote' | 'demote'
  reason?: string | null
  actor_email: string
  created_at: string
}

// Raised by `startMissionDay` when the SL rejects the start with a 409
// `ACTIVE_MISSION_EXISTS` envelope — the rep already has an active mission
// and the new one cannot be started until that one is finished. The
// `blockingMissionId` (from `details.blocking_mission_id`) lets the caller
// recover gracefully by resuming/navigating to the already-active mission
// instead of failing silently.
// (BUG-FE-TAGESPLANUNG-OPEN-SESSION-DIRECT-SUPABASE-RLS-AND-409-2026-06-07)
export const ACTIVE_MISSION_EXISTS_CODE = 'ACTIVE_MISSION_EXISTS' as const

export class ActiveMissionExistsError extends Error {
  readonly code = ACTIVE_MISSION_EXISTS_CODE
  constructor(
    readonly blockingMissionId: string | null,
    message = 'You already have an active mission',
  ) {
    super(message)
    this.name = 'ActiveMissionExistsError'
  }
}
